import { useState, useEffect, useRef } from 'react'

interface SlashCommandMenuProps {
  position: { top: number; left: number }
  onSelect: (command: string) => void
  onClose: () => void
}

const COMMANDS = [
  { id: 'heading1', label: 'Heading 1', desc: 'Large section heading', icon: 'H1' },
  { id: 'heading2', label: 'Heading 2', desc: 'Medium section heading', icon: 'H2' },
  { id: 'heading3', label: 'Heading 3', desc: 'Small section heading', icon: 'H3' },
  { id: 'bullet', label: 'Bullet List', desc: 'Simple unordered list', icon: '•' },
  { id: 'numbered', label: 'Numbered List', desc: 'Ordered list with numbers', icon: '1.' },
  { id: 'checklist', label: 'Task List', desc: 'Checklist with checkboxes', icon: '☐' },
  { id: 'code', label: 'Code Block', desc: 'Code snippet with syntax highlighting', icon: '</>' },
  { id: 'blockquote', label: 'Quote', desc: 'Blockquote for callouts', icon: '"' },
  { id: 'table', label: 'Table', desc: '3×3 table', icon: '⊞' },
  { id: 'image', label: 'Image', desc: 'Embed an image from URL', icon: '🖼' },
  { id: 'divider', label: 'Divider', desc: 'Horizontal separator line', icon: '—' },
  { id: 'ai-write', label: 'AI Write', desc: 'Generate content from a description', icon: '✨' },
]

export function SlashCommandMenu({ position, onSelect, onClose }: SlashCommandMenuProps) {
  const [filter, setFilter] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const menuRef = useRef<HTMLDivElement>(null)

  const filtered = COMMANDS.filter(c =>
    c.label.toLowerCase().includes(filter.toLowerCase()) ||
    c.desc.toLowerCase().includes(filter.toLowerCase())
  )

  useEffect(() => {
    setActiveIndex(0)
  }, [filter])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, filtered.length - 1)) }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)) }
      if (e.key === 'Enter') { e.preventDefault(); if (filtered[activeIndex]) onSelect(filtered[activeIndex].id) }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [filtered, activeIndex, onSelect, onClose])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  return (
    <div
      ref={menuRef}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: 280,
        maxHeight: 320,
        overflowY: 'auto',
        background: 'var(--bg-2)',
        border: '1px solid var(--border-h)',
        borderRadius: 10,
        boxShadow: 'var(--sh-lg)',
        zIndex: 50,
        padding: 4,
      }}
    >
      <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
        <input
          autoFocus
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter commands..."
          style={{
            width: '100%', background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--text-1)', fontSize: 13, fontFamily: 'var(--font)',
          }}
        />
      </div>
      {filtered.length === 0 ? (
        <div style={{ padding: '12px 8px', fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>
          No matching commands
        </div>
      ) : (
        filtered.map((cmd, i) => (
          <button
            key={cmd.id}
            onClick={() => onSelect(cmd.id)}
            onMouseEnter={() => setActiveIndex(i)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: '8px 10px', border: 'none', borderRadius: 6,
              background: i === activeIndex ? 'var(--accent-m)' : 'transparent',
              cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font)',
              transition: 'background 50ms',
            }}
          >
            <span style={{
              width: 28, height: 28, borderRadius: 6, display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              background: 'var(--bg-3)', fontSize: 12, fontWeight: 700,
              color: i === activeIndex ? 'var(--accent)' : 'var(--text-3)',
              fontFamily: 'var(--mono)',
            }}>{cmd.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)' }}>{cmd.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{cmd.desc}</div>
            </div>
          </button>
        ))
      )}
    </div>
  )
}
